// Hideo - script editor: the HTML of a script tab, without UI. Saved scripts are HTML (script.tabs[].html); these
// functions clean it when it is loaded, pasted from another Hideo script, written in the Markdown view or shown in the
// teleprompter, and fix older formats (checkboxes, links). cleanHtml is an allow-list (CLEAN_POLICY below): it keeps
// what the editor itself writes, byte for byte, and nothing else (golden tests: tests/editor-html.test.ts against the
// verbatim legacy copy in tests/fixtures/legacy/editor.js, attacks: tests/editor-sanitize.test.ts).
import { hrefScheme, safeLinkHref } from '../../utils/url'

/** The editor's page (#scriptBody): the default root of the fixes below */
export const edBodyEl = (): HTMLElement => document.getElementById('scriptBody') as HTMLElement
/** The top of the page area on screen (+8 px) in the --ed-top CSS variable: where the outline and the comments column
 *  open over the page between 768 and 1279 px (legacy.css; the toolbar above can take one or two rows) */
export function setEdTop(): void {
  const wrap = document.getElementById('scriptVisualWrap')
  if (wrap) document.documentElement.style.setProperty('--ed-top', wrap.getBoundingClientRect().top + 8 + 'px')
}

/** Removes the zero-width spaces (U+200B) the editor uses to step out of just-created bold or code */
export const noZw = (str: unknown): string => String(str || '').replace(/\u200b/g, '')

/** Removes the display-only readability marks (data-lowc, fixContrast in colors.ts) from saved or copied HTML */
export const stripLowc = (h: unknown): string => String(h || '').replace(/\sdata-lowc="[^"]*"/g, '')

/** What cleanHtml keeps. Elements not listed lose their tag and keep their content; `drop` ones go with their content. */
export interface CleanPolicy {
  /** Elements kept (lower-case tag names, HTML namespace only: svg and math always go; input only as a checkbox) */
  keep: ReadonlySet<string>
  /** Elements removed with everything inside (they win over `keep`) */
  drop: ReadonlySet<string>
  /** Attributes kept on every kept element (class and style are filtered further) */
  attrs: ReadonlySet<string>
  /** Attributes kept on one element ("tag": names) */
  tagAttrs: Readonly<Record<string, ReadonlySet<string>>>
  /** class tokens kept on every element; `iconClass` for the icon (<i>) of a mention chip */
  classes: ReadonlySet<string>
  iconClass: RegExp
  /** Inline style properties kept; `cssValue` checks each value (and, for some properties, which values) */
  css: ReadonlySet<string>
  cssValue: (prop: string, value: string) => boolean
  /** href check (src/utils/url.ts: relative, http, https, mailto, tel) */
  href: (v: string) => boolean
}

/** CSS functions a kept value may use (colours, calc); url(), var(), env(), attr(), image(), expression()… make the
 *  declaration go */
const CSS_FUNCS = new Set(['rgb', 'rgba', 'hsl', 'hsla', 'hwb', 'lab', 'lch', 'oklab', 'oklch', 'color', 'calc'])
/** Properties kept only with these values (Chrome writes "display: inline !important; float: none" when it copies) */
const CSS_ONLY: Readonly<Record<string, RegExp>> = { display: /^(inline|block|inline-block|none)(\s*!important)?$/i, float: /^none(\s*!important)?$/i }
function cssValueOk(prop: string, value: string): boolean {
  const v = value.trim()
  if (/[\\<>{}@\n\r\f]|\/\*|javascript:/i.test(v)) return false   // no escapes, comments, blocks, line breaks (they end a CSS string)
  for (const m of v.matchAll(/([a-z-]*)\s*\(/gi)) if (!CSS_FUNCS.has(m[1].toLowerCase())) return false
  const only = CSS_ONLY[prop]
  return !only || only.test(v)
}

const set = (s: string): ReadonlySet<string> => new Set(s.split(/\s+/).filter(Boolean))
/** The allow-list: what the editor writes (toolbar, "/" menu, Markdown, checklists, comments, mentions, links, colours,
 *  fonts, sizes, alignment, line spacing) plus the harmless formatting and structure that older scripts may contain
 *  (font, tables, h5/h6, section, dl, small, abbr…; title/dir/lang). None of these runs code, loads anything or uses
 *  the app's own classes. Docs: docs/features.md › Script editor › Saving. */
export const CLEAN_POLICY: CleanPolicy = {
  keep: set(`p div br hr h1 h2 h3 h4 h5 h6 blockquote pre code ul ol li details summary
    b strong i em u s strike del ins mark sub sup small span font a input
    table thead tbody tfoot tr td th caption colgroup col
    section article header footer main nav aside figure figcaption address dl dt dd center
    big kbd abbr cite q time var samp tt dfn bdi bdo wbr`),
  drop: set(`script style iframe frame frameset object embed applet param meta link base title head template noscript
    noembed noframes xmp plaintext form button select option optgroup datalist textarea output keygen
    img picture source video audio track canvas map area portal svg math slot`),
  attrs: set('class style title dir lang'),
  tagAttrs: {
    a: set('href target rel'), li: set('data-done value'), ol: set('start type reversed'), span: set('data-cmt data-ref contenteditable'),
    details: set('open'), font: set('color face size'), td: set('colspan rowspan'), th: set('colspan rowspan'), col: set('span'), colgroup: set('span'),
    input: set('type checked')
  },
  classes: set('todo callout cmt mention-chip mention-missing'),
  iconClass: /^(fa[srb]?|fa-[a-z0-9-]+)$/,
  css: set(`color background-color background font font-family font-size font-weight font-style font-stretch font-kerning
    font-variant font-variant-ligatures font-variant-caps font-variant-numeric font-variant-east-asian font-variant-position
    letter-spacing word-spacing line-height text-align text-indent text-transform white-space vertical-align direction
    text-decoration text-decoration-line text-decoration-style text-decoration-color text-decoration-thickness
    text-underline-offset orphans widows caret-color -webkit-text-stroke-width -webkit-text-stroke-color
    -webkit-text-fill-color display float`),
  cssValue: cssValueOk,
  href: safeLinkHref
}

/** Attribute values with only some values kept (anything else: the attribute goes) */
const ATTR_ONLY: Readonly<Record<string, RegExp>> = { target: /^_blank$/i, contenteditable: /^false$/i, dir: /^(ltr|rtl|auto)$/i, type: /^[\w-]*$/ }
const REL_OK = set('noopener noreferrer nofollow')
const HTML_NS = 'http://www.w3.org/1999/xhtml'

/** The style attribute with only the allowed declarations: unchanged when all are allowed, null when none is left */
function cleanStyleAttr(v: string, p: CleanPolicy): string | null {
  // Split at every ";": the browser ends a declaration only at a ";" outside strings and brackets, so each of its
  // declarations is one or more of these pieces, and starts with an allowed property
  const decls = v.split(';'), ok = decls.filter(d => {
    if (!d.trim()) return true
    const i = d.indexOf(':'), prop = (i < 0 ? d : d.slice(0, i)).trim().toLowerCase()
    return i > 0 && p.css.has(prop) && p.cssValue(prop, d.slice(i + 1))
  })
  if (ok.length === decls.length) return v
  const kept = ok.map(d => d.trim()).filter(Boolean)
  return kept.length ? kept.join('; ') : null
}
/** The class attribute with only the allowed tokens: unchanged when all are allowed, null when none is left */
function cleanClassAttr(v: string, tag: string, p: CleanPolicy): string | null {
  const all = v.split(/\s+/).filter(Boolean), ok = all.filter(c => p.classes.has(c) || (tag === 'i' && p.iconClass.test(c)))
  if (ok.length === all.length) return v
  return ok.length ? ok.join(' ') : null
}
function cleanAttrs(el: Element, tag: string, p: CleanPolicy): void {
  for (const a of [...el.attributes]) {
    const name = a.name.toLowerCase(), v = a.value
    let keep: string | null = null
    if (/^on/.test(name) || name === 'data-lowc' || /^(javascript|vbscript)$/.test(hrefScheme(v))) keep = null   // no value is a script, wherever it is
    else if (!p.attrs.has(name) && !(p.tagAttrs[tag] && p.tagAttrs[tag].has(name))) keep = null
    else if (name === 'class') keep = cleanClassAttr(v, tag, p)
    else if (name === 'style') keep = cleanStyleAttr(v, p)
    else if (name === 'href') keep = p.href(v) ? v : null
    else if (name === 'rel') { const all = v.split(/\s+/).filter(Boolean), ok = all.filter(r => REL_OK.has(r.toLowerCase())); keep = ok.length === all.length ? v : ok.length ? ok.join(' ') : null }
    else keep = ATTR_ONLY[name] && !ATTR_ONLY[name].test(v.trim()) ? null : v
    if (keep === null) el.removeAttribute(a.name)
    else if (keep !== v) el.setAttribute(a.name, keep)
  }
}
/** Cleans the children of a node in place, depth first: kept elements keep only their allowed attributes, the others
 *  are replaced by their (cleaned) content, dangerous ones and everything outside the HTML namespace go */
function cleanTree(node: ParentNode, p: CleanPolicy): void {
  for (const c of [...node.childNodes]) {
    if (c.nodeType === 3 || c.nodeType === 8) continue   // text and comments
    if (c.nodeType !== 1) { (c as ChildNode).remove(); continue }
    const el = c as Element, tag = el.tagName.toLowerCase()
    if (el.namespaceURI !== HTML_NS || p.drop.has(tag) || (tag === 'input' && !/^checkbox$/i.test(el.getAttribute('type') || ''))) { el.remove(); continue }
    cleanTree(el, p)
    if (!p.keep.has(tag)) { el.replaceWith(...el.childNodes); continue }
    cleanAttrs(el, tag, p)
  }
}

/** Cleans HTML that may not come from the editor (loaded scripts, Hideo copies pasted back, the Markdown view, the
 *  teleprompter, Markdown files, the guide) with a policy (CLEAN_POLICY by default). What the editor writes comes out
 *  byte for byte the same; the rest is unwrapped or removed: no scripts, frames, forms, images, svg/math, meta/link/base,
 *  no on… handlers, ids, unknown classes or data-…, no links other than relative / http(s) / mailto / tel, no inline
 *  styles other than text formatting (no url(), var(), position…). Checkboxes of older checklists stay for fixTodos. */
export function cleanWith(h: string, p: CleanPolicy): string {
  const tpl = document.createElement('template'); tpl.innerHTML = h
  cleanTree(tpl.content, p)
  return tpl.innerHTML
}
export const cleanHtml = (h: string): string => cleanWith(h, CLEAN_POLICY)

/** Checklist: ul.todo with ticked items marked by data-done (the box is drawn by the CSS, li::before). Converts the
 *  <input> checkboxes of older saved scripts: the item gets data-done when ticked, its list the "todo" class, and one
 *  space after the box goes. */
export function fixTodos(root: ParentNode = edBodyEl()): void {
  if (!root) return
  root.querySelectorAll<HTMLInputElement>('li > input[type=checkbox]').forEach(c => {
    const li = c.parentElement as HTMLElement
    if (c.checked || c.hasAttribute('checked')) li.setAttribute('data-done', '1')
    const ul = li.parentElement
    if (ul && ul.tagName === 'UL') ul.classList.add('todo')
    if (c.nextSibling && c.nextSibling.nodeType === 3) c.nextSibling.textContent = (c.nextSibling.textContent as string).replace(/^[\s ]/, '')
    c.remove()
  })
}

/** List items outside a list (a copy of some items of a list, before 24/10/2026, carried them without their list: pasted
 *  in a quote or a paragraph they showed their dots over the quote's line) go into a bulleted list, one per run of
 *  items next to each other */
export function wrapLoneItems(root: ParentNode): void {
  root.querySelectorAll('li').forEach(li => {
    const up = li.parentNode
    if (!up || /^(UL|OL)$/.test(up.nodeName)) return   // in a list (the run's next items are, once the first is moved)
    const ul = document.createElement('ul')
    li.before(ul)
    let n: ChildNode | null = li
    while (n && n.nodeName === 'LI') { const next: ChildNode | null = n.nextSibling; ul.appendChild(n); n = next }
  })
}

/** A list inside a quote (made by the editor's commands before 23/10/2026, or list items pasted into a quote) comes
 *  out of it: the quote is split around the list, so Enter and the lists' keys work as in any list. */
export function liftLists(root: ParentNode = edBodyEl()): void {
  if (!root) return
  wrapLoneItems(root)
  root.querySelectorAll('blockquote').forEach(q => {
    if (!q.querySelector(':scope > ul, :scope > ol')) return
    const out: Node[] = []
    let run: Node[] = []
    const flush = () => {
      while (run.length && run[run.length - 1].nodeName === 'BR') run.pop()
      while (run.length && run[0].nodeName === 'BR') run.shift()
      if (run.some(n => n.nodeType === 1 || (n.textContent as string).replace(/\u200b/g, '').trim())) { const b = q.cloneNode(false) as HTMLElement; b.append(...run); out.push(b) }
      run = []
    }
    ;[...q.childNodes].forEach(n => { if (/^(UL|OL)$/.test(n.nodeName)) { flush(); out.push(n) } else run.push(n) })
    flush()
    q.replaceWith(...out)
  })
}

/** Every link opens in a new tab, without access to the editor's page. Never throws (a link inside an <svg> has a
 *  read-only target in browsers): such links are skipped, so the editor always finishes loading. */
export function fixLinks(root: ParentNode = edBodyEl()): void {
  if (!root) return
  root.querySelectorAll('a').forEach(a => {
    if (a.namespaceURI !== HTML_NS) return
    try { a.target = '_blank'; a.rel = 'noopener' } catch { /* read-only: left as it is */ }
  })
}
